import React, { useState, useEffect, useCallback, useRef } from 'react';
import { computeAllActions, executeAction, loadReminders, completeReminder } from './actionEngine';
import './ActionCenter.css';

const TYPE_LABELS = {
  volume: 'Risk',
  flow: 'Sequence',
  template: 'Template',
  momentum: 'Focus',
};

const CONFIDENCE_LABELS = {
  high: 'High Confidence',
  medium: 'Early Signals',
};

const DISMISSED_KEY = 'actionCenter_dismissed';
const REFRESH_INTERVAL = 5 * 60 * 1000;

const formatDueLabel = (dueAt) => {
  if (!dueAt) return '';
  const diffMs = dueAt - Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.round(diffMs / dayMs);
  if (diffMs < 0) {
    const overdue = Math.abs(days);
    if (overdue === 0) return 'Overdue today';
    return `${overdue} day${overdue === 1 ? '' : 's'} overdue`;
  }
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 14) return `In ${days} days`;
  if (days < 60) return `In ${Math.round(days / 7)} weeks`;
  return `In ${Math.round(days / 30)} months`;
};

const ActionCenter = ({ userId, onFilterSegment, onOpenContact }) => {
  const [actions, setActions] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [executingId, setExecutingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const intervalRef = useRef(null);
  const panelRef = useRef(null);

  const loadActions = useCallback(async () => {
    if (!userId) return;
    try {
      const [result, reminderList] = await Promise.all([
        computeAllActions(userId),
        loadReminders(userId),
      ]);
      setActions(result);
      setReminders(reminderList);
    } catch (e) {
      console.warn('[ActionCenter] load error:', e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadActions();
    intervalRef.current = setInterval(loadActions, REFRESH_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [loadActions]);

  // Refresh when the panel is opened so freshly-created reminders show up.
  useEffect(() => {
    if (open) loadActions();
  }, [open, loadActions]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleDismiss = (actionId) => {
    const next = { ...dismissed, [actionId]: Date.now() };
    setDismissed(next);
    try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(next)); } catch {}
  };

  const handleExecute = async (action) => {
    if (!action.action) return;
    setExecutingId(action.id);

    if (action.action.executor === 'filterToSegment' && onFilterSegment) {
      onFilterSegment(action.action.payload);
      handleDismiss(action.id);
      setExecutingId(null);
      setOpen(false);
      return;
    }

    const ok = await executeAction(userId, action);
    if (ok) handleDismiss(action.id);
    setExecutingId(null);
  };

  const handleCompleteReminder = async (reminderId) => {
    setCompletingId(reminderId);
    const ok = await completeReminder(userId, reminderId);
    if (ok) setReminders(prev => prev.filter(r => r.id !== reminderId));
    setCompletingId(null);
  };

  const handleOpenReminder = (reminder) => {
    if (onOpenContact && reminder.contactId) {
      onOpenContact(reminder.contactId);
      setOpen(false);
    }
  };

  // Dismissal of computed alerts is permanent per specific alert id.
  // Bounce / complaint alerts carry a severity tier in their id, so an
  // escalation (e.g. warn → crit) surfaces again as a new alert.
  const visibleActions = actions.filter(a => !a.id || !dismissed[a.id]);

  const dueReminders = reminders.filter(r => (r.dueAt || 0) <= Date.now());
  const hasRisk = visibleActions.some(a => a.risk);
  const hasDueReminder = dueReminders.length > 0;
  const count = visibleActions.length + reminders.length;

  return (
    <div className="ac-fab-wrapper" ref={panelRef}>
      {open && (
        <div className="ac-panel">
          <div className="ac-panel-header">
            <span className="ac-panel-title">Action Center</span>
            <button className="ac-panel-close" onClick={() => setOpen(false)}>&times;</button>
          </div>

          {loading ? (
            <div className="ac-panel-empty">Analyzing...</div>
          ) : count === 0 ? (
            <div className="ac-panel-empty">
              <span className="ac-panel-check">&#10003;</span>
              All good. Keep it up.
            </div>
          ) : (
            <div className="ac-panel-cards">
              {reminders.length > 0 && (
                <div className="ac-reminders">
                  <div className="ac-section-label">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    Reminders
                  </div>
                  {reminders.map(reminder => {
                    const isDue = (reminder.dueAt || 0) <= Date.now();
                    return (
                      <div key={reminder.id} className={`ac-reminder ${isDue ? 'due' : ''}`}>
                        <div className="ac-reminder-main">
                          <div className="ac-reminder-msg">{reminder.message || 'Follow up'}</div>
                          <div className="ac-reminder-meta">
                            {reminder.contactName && <span className="ac-reminder-contact">{reminder.contactName}</span>}
                            <span className={`ac-reminder-due ${isDue ? 'due' : ''}`}>{formatDueLabel(reminder.dueAt)}</span>
                          </div>
                        </div>
                        <div className="ac-reminder-actions">
                          {onOpenContact && reminder.contactId && (
                            <button className="ac-reminder-btn open" onClick={() => handleOpenReminder(reminder)} title="Open contact">
                              Open
                            </button>
                          )}
                          <button
                            className="ac-reminder-btn done"
                            onClick={() => handleCompleteReminder(reminder.id)}
                            disabled={completingId === reminder.id}
                            title="Mark done"
                          >
                            {completingId === reminder.id ? '...' : 'Done'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {visibleActions.length > 0 && reminders.length > 0 && (
                <div className="ac-section-label ac-section-label-alerts">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  Alerts
                </div>
              )}
              {visibleActions.map(action => {
                const cardClass = action.risk ? 'risk' : action.type;
                return (
                  <div key={action.id} className={`ac-card ${cardClass}`}>
                    <div className="ac-card-top">
                      <span className={`ac-card-type ${action.type}`}>
                        {TYPE_LABELS[action.type] || action.type}
                      </span>
                      <span className={`ac-card-conf ${action.confidence}`}>
                        {CONFIDENCE_LABELS[action.confidence] || ''}
                      </span>
                    </div>
                    <div className="ac-card-title">{action.title}</div>
                    <div className="ac-card-body">{action.body}</div>
                    <div className="ac-card-footer">
                      {action.action && (
                        <button
                          className={`ac-card-btn primary ${action.confidence === 'medium' ? 'medium-conf' : ''}`}
                          onClick={() => handleExecute(action)}
                          disabled={executingId === action.id}
                        >
                          {executingId === action.id ? '...' : action.action.label}
                        </button>
                      )}
                      {action.dismiss && (
                        <button
                          className="ac-card-btn dismiss"
                          onClick={() => handleDismiss(action.id)}
                          title="Dismiss"
                        >
                          &#10005;
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <button
        className={`ac-fab ${hasRisk ? 'ac-fab-risk' : ''} ${!hasRisk && hasDueReminder ? 'ac-fab-reminder' : ''} ${open ? 'ac-fab-open' : ''}`}
        onClick={() => setOpen(!open)}
        title="Action Center"
      >
        {open ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        )}
        {!open && count > 0 && (
          <span className={`ac-fab-badge ${hasRisk ? 'risk' : ''} ${!hasRisk && hasDueReminder ? 'reminder' : ''}`}>{count}</span>
        )}
      </button>
    </div>
  );
};

export default ActionCenter;
